import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import PaymentAnalytics from "@/components/PaymentAnalytics";

import { 
  ArrowLeft, DollarSign, Plus, Trash2, 
  CheckCircle, Clock, CreditCard, Calendar,
  TrendingUp, Users, Filter, BarChart3, Receipt
} from "lucide-react";

export const PaymentTrackerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total_received: 0, total_pending: 0, total_count: 0 });
  const [clients, setClients] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("analytics");
  const [formData, setFormData] = useState({
    client_id: "",
    amount: "",
    description: "",
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: "invoice",
    status: "received",
    notes: ""
  });

  useEffect(() => {
    fetchPayments();
    fetchClients();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await authFetch(`${API}/consultant/payments`, {  });
      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
        setSummary(data.summary || { total_received: 0, total_pending: 0, total_count: 0 });
      }
    } catch (_error) {
      console.error("Error fetching payments:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await authFetch(`${API}/consultant/clients`, {  });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (_error) {
      console.error("Error fetching clients:", _error);
    }
  };

  const handleAddPayment = async () => {
    if (!formData.client_id || !formData.amount) {
      toast.error("Client and amount are required");
      return;
    }

    try {
      const response = await authFetch(`${API}/consultant/clients/${formData.client_id}/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount)
        })
      });

      if (response.ok) {
        toast.success("Payment recorded");
        setShowAddDialog(false);
        setFormData({
          client_id: "",
          amount: "",
          description: "",
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: "invoice",
          status: "received",
          notes: ""
        });
        fetchPayments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add payment");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!confirm("Delete this payment record?")) return;

    try {
      const response = await authFetch(`${API}/consultant/payments/${paymentId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("Payment deleted");
        fetchPayments();
      }
    } catch (_error) {
      toast.error("Failed to delete payment");
    }
  };

  const filteredPayments = payments.filter(p => 
    filterStatus === "all" || p.status === filterStatus
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="payment-tracker-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Payments & Analytics</h1>
                <p className="text-sm text-neutral-500">Track revenue and manage payments</p>
              </div>
            </div>
            {activeTab === "tracker" && (
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#333333] hover:bg-[#444444] text-white" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Record Payment</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Client *</Label>
                      <Select
                        value={formData.client_id}
                        onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      >
                        <SelectTrigger data-testid="payment-client-select">
                          <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.user_id} value={client.user_id}>
                            {client.name} ({client.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Amount ($) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="0.00"
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="e.g., Monthly consulting fee"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Method</Label>
                      <Select
                        value={formData.payment_method}
                        onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="invoice">Invoice</SelectItem>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes..."
                      className="min-h-[60px]"
                    />
                  </div>

                  <Button 
                    onClick={handleAddPayment}
                    disabled={!formData.client_id || !formData.amount}
                    className="w-full bg-[#333333] hover:bg-[#444444] text-white"
                    data-testid="submit-payment-btn"
                  >
                    Record Payment
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-neutral-100">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Stripe Analytics
            </TabsTrigger>
            <TabsTrigger value="tracker" className="data-[state=active]:bg-white" data-testid="tab-tracker">
              <Receipt className="w-4 h-4 mr-2" />
              Manual Payments
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <PaymentAnalytics />
          </TabsContent>

          {/* Manual Payment Tracker Tab */}
          <TabsContent value="tracker">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <Badge className="bg-green-100 text-green-700">Received</Badge>
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">${summary.total_received.toLocaleString()}</p>
                  <p className="text-sm text-neutral-500">Total Received</p>
                </CardContent>
              </Card>

              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-6 h-6 text-amber-600" />
                    <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">${summary.total_pending.toLocaleString()}</p>
                  <p className="text-sm text-neutral-500">Pending Payments</p>
                </CardContent>
              </Card>

              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                    <Badge className="bg-blue-100 text-blue-700">Total</Badge>
                  </div>
                  <p className="text-3xl font-bold text-neutral-900">${(summary.total_received + summary.total_pending).toLocaleString()}</p>
                  <p className="text-sm text-neutral-500">{summary.total_count} Transactions</p>
            </CardContent>
          </Card>
        </div>

        {/* Payments List */}
        <Card className="border-neutral-200">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Payment History</CardTitle>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-neutral-400" />
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Payments</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-neutral-900 mb-2">No Payments Yet</h3>
                <p className="text-neutral-500 mb-4">Start tracking client payments</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Record First Payment
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPayments.map((payment) => (
                  <div 
                    key={payment.payment_id}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        payment.status === "received" ? "bg-green-100" :
                        payment.status === "pending" ? "bg-amber-100" : "bg-blue-100"
                      }`}>
                        <DollarSign className={`w-5 h-5 ${
                          payment.status === "received" ? "text-green-600" :
                          payment.status === "pending" ? "text-amber-600" : "text-blue-600"
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">
                          {payment.client?.name || "Unknown Client"}
                        </p>
                        <p className="text-sm text-neutral-500">
                          {payment.description || payment.payment_method}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="font-bold text-neutral-900">${payment.amount.toLocaleString()}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(payment.payment_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={
                        payment.status === "received" ? "bg-green-100 text-green-700" :
                        payment.status === "pending" ? "bg-amber-100 text-amber-700" :
                        "bg-blue-100 text-blue-700"
                      }>
                        {payment.status}
                      </Badge>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-8 w-8 p-0 text-neutral-400 hover:text-red-500"
                        onClick={() => handleDeletePayment(payment.payment_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PaymentTrackerPage;
